import type {
  CatalogAuditRecord,
  CatalogProduct,
  CreateCategoryInput,
  CreateProductInput,
  DuplicateProductInput,
  PaginatedResult,
  ProductCategory,
  UpdateCategoryInput,
  UpdateProductInput,
} from '@emazy/shared-types';
import {
  createCategorySchema,
  createProductSchema,
  duplicateProductSchema,
  updateCategorySchema,
  updateProductSchema,
} from '@emazy/shared-validation';
import { z } from 'zod';

import { ApiClient } from '@/lib/api';

import { createAdminCatalogError } from './adminCatalogErrors';
import {
  adminCategorySchema,
  adminIdSchema,
  adminProductSchema,
  auditInputSchema,
  auditRecordSchema,
  lifecycleInputSchema,
  listInputSchema,
  pageSchema,
  productListInputSchema,
} from './adminCatalogSchemas';
import type {
  AdminCatalogApi,
  AdminCategoryListInput,
  AdminProductListInput,
  AuditListInput,
  CatalogLifecycleInput,
} from './types';
import { AdminCatalogError } from './types';

type ErrorEnvelope = {
  error?: { code?: string; fieldErrors?: Record<string, string[]> };
  requestId?: string;
};

export class HttpAdminCatalogApi implements AdminCatalogApi {
  constructor(private readonly client: ApiClient) {}

  private async request<T>(
    path: string,
    schema: z.ZodType<T>,
    options: RequestInit = {},
  ): Promise<T> {
    const headers = new Headers(options.headers);
    if (options.body) headers.set('content-type', 'application/json');
    let response: Response;
    try {
      response = await this.client.request(path, {
        ...options,
        headers,
        auth: 'protected',
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new AdminCatalogError(
        'network-error',
        'The catalog service could not be reached.',
        undefined,
        undefined,
        undefined,
        { cause: error },
      );
    }
    const payload = (await response.json().catch(() => ({}))) as ErrorEnvelope & {
      data?: unknown;
    };
    if (!response.ok)
      throw createAdminCatalogError({
        ...(payload.error?.code ? { code: payload.error.code } : {}),
        status: response.status,
        ...(payload.error?.fieldErrors ? { fieldErrors: payload.error.fieldErrors } : {}),
        ...(payload.requestId ? { requestId: payload.requestId } : {}),
      });
    try {
      return schema.parse(payload.data);
    } catch (error) {
      throw new AdminCatalogError(
        'unknown',
        'The catalog returned invalid administration data.',
        undefined,
        undefined,
        payload.requestId,
        { cause: error },
      );
    }
  }

  listCategories(input: AdminCategoryListInput, signal?: AbortSignal) {
    const parsed = listInputSchema.parse(input);
    return this.request<PaginatedResult<ProductCategory>>(
      `/v1/admin/catalog/categories${query(parsed)}`,
      pageSchema(adminCategorySchema),
      signal ? { signal } : {},
    );
  }
  getCategory(id: string, signal?: AbortSignal) {
    return this.request<ProductCategory>(
      `/v1/admin/catalog/categories/${encodeURIComponent(adminIdSchema.parse(id))}`,
      adminCategorySchema,
      signal ? { signal } : {},
    );
  }
  createCategory(input: CreateCategoryInput) {
    return this.mutate(
      '/v1/admin/catalog/categories',
      'POST',
      createCategorySchema.parse(input),
      adminCategorySchema,
    );
  }
  updateCategory(id: string, input: UpdateCategoryInput) {
    return this.mutate(
      `/v1/admin/catalog/categories/${encodeURIComponent(adminIdSchema.parse(id))}`,
      'PUT',
      updateCategorySchema.parse(input),
      adminCategorySchema,
    );
  }
  publishCategory(id: string, input: CatalogLifecycleInput) {
    return this.lifecycle('categories', id, 'publish', input, adminCategorySchema);
  }
  archiveCategory(id: string, input: CatalogLifecycleInput) {
    return this.lifecycle('categories', id, 'archive', input, adminCategorySchema);
  }
  listCategoryAudit(id: string, input: AuditListInput = {}, signal?: AbortSignal) {
    const parsed = auditInputSchema.parse(input);
    return this.request<PaginatedResult<CatalogAuditRecord>>(
      `/v1/admin/catalog/categories/${encodeURIComponent(adminIdSchema.parse(id))}/audit${query(parsed)}`,
      pageSchema(auditRecordSchema),
      signal ? { signal } : {},
    );
  }
  listProducts(input: AdminProductListInput, signal?: AbortSignal) {
    const parsed = productListInputSchema.parse(input);
    return this.request<PaginatedResult<CatalogProduct>>(
      `/v1/admin/catalog/products${query(parsed)}`,
      pageSchema(adminProductSchema),
      signal ? { signal } : {},
    );
  }
  getProduct(id: string, signal?: AbortSignal) {
    return this.request<CatalogProduct>(
      `/v1/admin/catalog/products/${encodeURIComponent(adminIdSchema.parse(id))}`,
      adminProductSchema,
      signal ? { signal } : {},
    );
  }
  createProduct(input: CreateProductInput) {
    return this.mutate(
      '/v1/admin/catalog/products',
      'POST',
      createProductSchema.parse(input),
      adminProductSchema,
    );
  }
  updateProduct(id: string, input: UpdateProductInput) {
    return this.mutate(
      `/v1/admin/catalog/products/${encodeURIComponent(adminIdSchema.parse(id))}`,
      'PUT',
      updateProductSchema.parse(input),
      adminProductSchema,
    );
  }
  publishProduct(id: string, input: CatalogLifecycleInput) {
    return this.lifecycle('products', id, 'publish', input, adminProductSchema);
  }
  archiveProduct(id: string, input: CatalogLifecycleInput) {
    return this.lifecycle('products', id, 'archive', input, adminProductSchema);
  }
  duplicateProduct(id: string, input: DuplicateProductInput) {
    return this.mutate(
      `/v1/admin/catalog/products/${encodeURIComponent(adminIdSchema.parse(id))}/duplicate`,
      'POST',
      duplicateProductSchema.parse(input),
      adminProductSchema,
    );
  }
  listProductAudit(id: string, input: AuditListInput = {}, signal?: AbortSignal) {
    const parsed = auditInputSchema.parse(input);
    return this.request<PaginatedResult<CatalogAuditRecord>>(
      `/v1/admin/catalog/products/${encodeURIComponent(adminIdSchema.parse(id))}/audit${query(parsed)}`,
      pageSchema(auditRecordSchema),
      signal ? { signal } : {},
    );
  }

  private mutate<T>(path: string, method: 'POST' | 'PUT', input: unknown, schema: z.ZodType<T>) {
    return this.request(path, schema, { method, body: JSON.stringify(input) });
  }
  private lifecycle<T>(
    kind: 'categories' | 'products',
    id: string,
    action: 'publish' | 'archive',
    input: CatalogLifecycleInput,
    schema: z.ZodType<T>,
  ) {
    return this.mutate(
      `/v1/admin/catalog/${kind}/${encodeURIComponent(adminIdSchema.parse(id))}/${action}`,
      'POST',
      lifecycleInputSchema.parse(input),
      schema,
    );
  }
}

function query(values: Record<string, unknown>) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values))
    if (value !== undefined && value !== '') params.set(key, String(value));
  return params.size ? `?${params.toString()}` : '';
}
